// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { appSelect, createDataProvider } from '@shared/ui-kit';
import { tagsApi } from '@shared/data-access/api/engine/tags-api';
import {
  selectAllTags,
  selectTagsAssignments,
  generateAssignmentId,
} from '@shared/data-access/slices/tags-management';
import type {
  TagEntity,
  TagsByObjectEntry,
  TagsByObjectKey,
} from '@shared/data-access/interfaces/redux/tags-management-state-types';
import type { TagOwnerType } from '@shared/data-access/types/engine-types';

export const useTagManagementFactory = () => {
  const ownerType = appSelect({
    advisor: 'USER',
    client: 'CLIENT',
  }) as TagOwnerType;

  const tagsQuery = tagsApi.useGetTagsQuery({ owner_type: ownerType });

  const tagsAssignmentsQuery = tagsApi.useSearchTagAssignmentsQuery({
    owner_type: ownerType,
  });

  const tags = useSelector(selectAllTags);

  const tagsAssignments = useSelector(selectTagsAssignments);

  const tagsByObject = useMemo((): Map<TagsByObjectKey, TagsByObjectEntry> => {
    const tagById = new Map(tags.map(t => [t.tag_id, t]));

    const entries = tagsAssignments.map(assignment => {
      const resolvedTags = assignment.tag_ids
        .map(id => tagById.get(id))
        .filter((t): t is TagEntity => t !== null);

      const key = generateAssignmentId(
        assignment.object_type,
        assignment.object_id,
      ) as TagsByObjectKey;

      const value: TagsByObjectEntry = {
        id: assignment.object_id,
        type: assignment.object_type,
        tags: resolvedTags,
      };

      return [key, value] as const;
    });

    return new Map(entries);
  }, [tags, tagsAssignments]);

  return {
    tagsQuery,
    tagsAssignmentsQuery,
    tags,
    tagsAssignments,
    tagsByObject,
  };
};

export const { TagManagementProvider, useTagManagementData } = createDataProvider({
  factory: useTagManagementFactory,
  name: 'TagManagement',
});
